// useFirebaseUpload.jsx



// hooks/useFirebaseUpload.jsx
import { getDatabase, ref, push, set, update } from 'firebase/database';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { useAuth } from './useAuth';

const useFirebaseUpload = () => {
  const { currentUser } = useAuth();
  const database = getDatabase();
  const storage = getStorage();

  /**
   * @param {Object} propertyData – All listing fields (may already include images)
   * @param {Array}  images       – May contain:
   *                                 • raw URL strings  ➜ keep as-is
   *                                 • {file: File}     ➜ upload & get URL
   *                                 • {preview: URL}   ➜ keep its URL
   */
  const uploadProperty = async (propertyData, images = []) => {
    if (!currentUser) throw new Error('User is not authenticated.');
    const userId = currentUser.uid;

    try {
      /* ───── reference ───── */
      const propertyRef = propertyData.id
        ? ref(database, `listings/${propertyData.id}`)
        : push(ref(database, 'listings'));
      const propertyId = propertyRef.key;

      /* ───── sanitise nullable fields ───── */
      const cleanData = {
        ...propertyData,
        state: propertyData.state || null,
        city: propertyData.city || null,
        deposit: propertyData.deposit || null,
        applicationFee: propertyData.applicationFee || null,
        applicationLink: propertyData.applicationLink || null,
        qualifyingCriteria: propertyData.qualifyingCriteria || null,
        schedulingRestrictions: propertyData.schedulingRestrictions || null,
        specialInstructions: propertyData.specialInstructions || null,
      };

      /* ───── handle images ─────
         • raw URL  ➜ keep
         • new File ➜ upload
         • preview  ➜ keep
      */
      const imageUrls = await Promise.all(
        images.map(async (img) => {
          if (!img) return null;

          // Already a stored URL
          if (typeof img === 'string') return img;

          // Newly-selected file ➜ upload to Firebase Storage
          if (img.file instanceof File) {
            const imgRef = storageRef(storage, `listings/${propertyId}/${img.file.name}`);
            const snap = await uploadBytes(imgRef, img.file);
            return getDownloadURL(snap.ref);
          }

          // Existing preview URL from previous upload flow
          if (img.preview) return img.preview;

          return null;
        })
      ).then((urls) => urls.filter(Boolean)); // remove null / undefined

      // Fallback to any images already present on the record
      const finalImages = imageUrls.length > 0 ? imageUrls : cleanData.images || null;
      const finalMainImage =
        finalImages && finalImages.length > 0
          ? finalImages[0]
          : cleanData.mainImage || (cleanData.images ? cleanData.images[0] : null);

      /* ───── final payload ───── */
      const propertyDetails = {
        ...cleanData,
        ownerId: userId,
        images: finalImages,
        mainImage: finalMainImage,
        agentPhone: propertyData.agentPhone || null,
        updatedAt: Date.now(),
      };

      /* ───── write to Realtime DB ───── */
      await set(propertyRef, propertyDetails);

      /* ───── link listing to user ───── */
      const userListingsRef = ref(database, `users/${userId}/listings`);
      await update(userListingsRef, { [propertyId]: true });

      return { success: true, propertyId };
    } catch (err) {
      console.error('Error uploading property:', err);
      throw err;
    }
  };

  return { uploadProperty };
};

export default useFirebaseUpload;

// import { getDatabase, ref, push, set, update } from 'firebase/database';
// import {
//   getStorage,
//   ref as storageRef,
//   uploadBytes,
//   getDownloadURL,
// } from 'firebase/storage';
// import { useAuth } from './useAuth';

// const useFirebaseUpload = () => {
//   const { currentUser } = useAuth();
//   const database = getDatabase();
//   const storage   = getStorage();

//   /**
//    * @param {Object}  propertyData         – All listing fields (may already include images)
//    * @param {Array}   images               – May contain:
//    *                                          • raw URL strings  ➜ keep as-is
//    *                                          • {file: File}     ➜ upload & get URL
//    *                                          • {preview: URL}   ➜ keep its URL
//    * @param {File?}   proofOfOwnershipFile – Optional PDF / image proof
//    */
//   const uploadProperty = async (
//     propertyData,
//     images = [],
//     proofOfOwnershipFile = null
//   ) => {
//     if (!currentUser) throw new Error('User is not authenticated.');

//     const userId = currentUser.uid;

//     try {
//       /* ───── reference ───── */
//       const propertyRef = propertyData.id
//         ? ref(database, `listings/${propertyData.id}`)
//         : push(ref(database, 'listings'));
//       const propertyId  = propertyRef.key;

//       /* ───── sanitise nullable fields ───── */
//       const cleanData = {
//         ...propertyData,
//         state: propertyData.state || null,
//         city: propertyData.city || null,
//         deposit: propertyData.deposit || null,
//         applicationFee: propertyData.applicationFee || null,
//         applicationLink: propertyData.applicationLink || null,
//         qualifyingCriteria: propertyData.qualifyingCriteria || null,
//         schedulingRestrictions: propertyData.schedulingRestrictions || null,
//         specialInstructions: propertyData.specialInstructions || null,
//       };

//       /* ───── handle images ─────
//          • raw URL  ➜ keep
//          • new File ➜ upload
//          • preview  ➜ keep
//       */
//       const imageUrls = await Promise.all(
//         images.map(async (img) => {
//           if (!img) return null;

//           // Already a stored URL
//           if (typeof img === 'string') return img;

//           // Newly-selected file ➜ upload to Firebase Storage
//           if (img.file instanceof File) {
//             const imgRef = storageRef(storage, `listings/${propertyId}/${img.file.name}`);
//             const snap   = await uploadBytes(imgRef, img.file);
//             return getDownloadURL(snap.ref);
//           }

//           // Existing preview URL from previous upload flow
//           if (img.preview) return img.preview;

//           return null;
//         })
//       ).then((urls) => urls.filter(Boolean)); // remove null / undefined

//       // Fallback to any images already present on the record
//       const finalImages    = imageUrls.length > 0 ? imageUrls : cleanData.images || null;
//       const finalMainImage =
//         finalImages && finalImages.length > 0
//           ? finalImages[0]
//           : cleanData.mainImage || (cleanData.images ? cleanData.images[0] : null);

//       /* ───── proof of ownership (optional) ───── */
//       let proofOfOwnershipUrl = null;
//       if (proofOfOwnershipFile) {
//         const proofRef = storageRef(
//           storage,
//           `listings/${propertyId}/proofOfOwnership/${proofOfOwnershipFile.name}`
//         );
//         const snap = await uploadBytes(proofRef, proofOfOwnershipFile);
//         proofOfOwnershipUrl = await getDownloadURL(snap.ref);
//       }

//       /* ───── final payload ───── */
//       const propertyDetails = {
//         ...cleanData,
//         ownerId: userId,
//         images: finalImages,
//         mainImage: finalMainImage,
//         documents: {
//           proofOfOwnership:
//             proofOfOwnershipUrl ||
//             propertyData.documents?.proofOfOwnership ||
//             null,
//         },
//         agentPhone: propertyData.agentPhone || null,
//         updatedAt: Date.now(),
//       };

//       /* ───── write to Realtime DB ───── */
//       await set(propertyRef, propertyDetails);

//       /* ───── link listing to user ───── */
//       const userListingsRef = ref(database, `users/${userId}/listings`);
//       await update(userListingsRef, { [propertyId]: true });

//       return { success: true, propertyId };
//     } catch (err) {
//       console.error('Error uploading property:', err);
//       throw err;
//     }
//   };

//   return { uploadProperty };
// };

// export default useFirebaseUpload;
